/**
 * Messages Page - Trade Loop v2.0
 *
 * In-app messaging center for conversations between users
 * Includes partner messages and connection requests
 */

import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import AppLayout from "@/components/layout/AppLayout";
import ReferralDetailModal from "@/components/ReferralDetailModal";
import ProfilePreviewModal from "@/components/ProfilePreviewModal";
import AttachmentViewerModal from "@/components/AttachmentViewerModal";
import { ReportContentModal } from "@/components/ReportContentModal";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { useOrganization } from "../hooks/useOrganization";
import { api, ApiError } from "../lib/api";
import { usePublicProfile, toProfilePreviewPartner } from "@/api/extensions/usePublicProfile";
import { usePartnerConnection } from "@/api/extensions/usePartnerConnection";
import { supabase } from "../lib/supabase";
import {
  classifyMediaFile,
  describeUploadError,
  VIDEO_FILE_EXTENSIONS,
  IMAGE_FILE_EXTENSIONS,
} from "../lib/mediaUploadValidation";
import { captureImageInto, isNativeCaptureAvailable } from "@/services/mediaCapture";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  MessageSquare,
  Search,
  UserPlus,
  Send,
  ArrowLeft,
  Check,
  X,
  User,
  MoreVertical,
  Flag,
  Trash2,
  Pencil,
  Forward,
  Smile,
  ChevronDown,
  ChevronRight,
  FileText,
  Users,
  Paperclip,
  Camera,
  Image as ImageIcon,
  File,
  Download,
} from "lucide-react";

// Message types
type MessageType = "conversation" | "connection_request";

interface Message {
  // Composite conversation id (`${chat_id}` for direct, `${chat_id}::ref-${id}`
  // for a referral thread). Use this for cache keys and API paths.
  id: string;
  // Raw chat_id (sorted user-pair). Use this for the Supabase realtime filter,
  // since postgres_changes doesn't understand the composite suffix.
  chatId?: string;
  type: MessageType;
  partnerId: string;
  partnerName: string;
  partnerTrade?: string;
  partnerAvatar?: string;
  // Bug 23: the partner permanently deleted their account. The thread and its attachments
  // survive — this user's own history — but the other end no longer exists: partnerName is the
  // neutral "Former Trade Loop User" label, the profile cannot be opened, and nothing further
  // can be sent.
  partnerDeleted?: boolean;
  // Company name as it stood when they deleted, where retained. Shown instead of the trade so
  // the thread is still recognisable.
  partnerFormerCompany?: string | null;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  status?: "pending" | "accepted" | "declined";
  isNewConversation?: boolean; // Flag for placeholder conversations before first message
  isOrgAdmin?: boolean; // Flag if this partner is the user's organization admin
  referralId?: string | number; // Set when this conversation is about a specific referral
}

// Conversation ids are partitioned by referral so direct and per-referral
// threads between the same two users stay isolated. Mirrors the backend.
const COMPOSITE_REFERRAL_SEPARATOR = "::ref-";

function computeChatId(userIdA: string, userIdB: string): string {
  return [userIdA, userIdB].sort().join("_");
}

function buildConversationId(
  chatId: string,
  referralId: string | number | null | undefined,
): string {
  return referralId
    ? `${chatId}${COMPOSITE_REFERRAL_SEPARATOR}${referralId}`
    : chatId;
}

function extractRawChatId(conversationId: string): string {
  const idx = conversationId.indexOf(COMPOSITE_REFERRAL_SEPARATOR);
  return idx === -1 ? conversationId : conversationId.slice(0, idx);
}

interface MessageReaction {
  emoji: string;
  userId: string;
  userName: string;
  id: string;
}

interface ChatMessage {
  id: string;
  senderId: string;
  senderName?: string;
  content: string;
  timestamp: string;
  isOwn: boolean;
  editedAt?: string | null;
  reactions?: MessageReaction[];
  messageType?: 'text' | 'system';
}

const REACTION_EMOJIS = ["❤️", "😊", "😂", "😢", "😭", "👍", "👌", "💯"];

// Mock data
const mockConversations: Message[] = [
  {
    id: "conv-1",
    type: "conversation",
    partnerId: "p-1",
    partnerName: "John Smith",
    partnerTrade: "Electrician",
    lastMessage: "Thanks for the referral! I'll follow up with them today.",
    lastMessageTime: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    unreadCount: 2,
  },
  {
    id: "conv-2",
    type: "connection_request",
    partnerId: "p-2",
    partnerName: "Sarah Johnson",
    partnerTrade: "Plumber",
    lastMessage: "Hi! I'd love to connect and share referrals.",
    lastMessageTime: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    unreadCount: 1,
    status: "pending",
  },
  {
    id: "conv-3",
    type: "conversation",
    partnerId: "p-3",
    partnerName: "Mike's HVAC",
    partnerTrade: "HVAC",
    lastMessage: "The job went great, client was very happy!",
    lastMessageTime: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
    unreadCount: 0,
  },
  {
    id: "conv-4",
    type: "connection_request",
    partnerId: "p-4",
    partnerName: "Elite Roofing Co",
    partnerTrade: "Roofing",
    lastMessage: "We specialize in residential roofing. Let's partner up!",
    lastMessageTime: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    unreadCount: 1,
    status: "pending",
  },
  {
    id: "conv-5",
    type: "conversation",
    partnerId: "p-5",
    partnerName: "ABC Electric",
    partnerTrade: "Electrician",
    lastMessage: "Do you have any leads in the downtown area?",
    lastMessageTime: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    unreadCount: 0,
  },
];

const mockChatMessages: ChatMessage[] = [
  {
    id: "msg-1",
    senderId: "p-1",
    content: "Hey! I got your referral for the kitchen remodel.",
    timestamp: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    isOwn: false,
  },
  {
    id: "msg-2",
    senderId: "me",
    content: "Great! They're a good client, should be straightforward work.",
    timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    isOwn: true,
  },
  {
    id: "msg-3",
    senderId: "p-1",
    content: "Perfect. I'll reach out to them this afternoon.",
    timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    isOwn: false,
  },
  {
    id: "msg-4",
    senderId: "p-1",
    content: "Thanks for the referral! I'll follow up with them today.",
    timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    isOwn: false,
  },
];

// Attachment Display Component
const AttachmentDisplay = ({
  attachment,
  isOwn,
  siblingAttachments,
  onOpenGallery,
  onLoaded,
}: {
  attachment: any;
  isOwn: boolean;
  siblingAttachments?: any[];
  onOpenGallery?: (attachments: any[], tapped: any) => void;
  onLoaded?: () => void;
}) => {
  // Use React Query to cache signed URLs
  // Cache for 50 minutes (URLs expire in 1 hour, so refresh before expiry)
  const { data: url, isLoading: loading } = useQuery({
    queryKey: ['attachment-url', attachment.storagePath],
    queryFn: async () => {
      console.log('[Attachment] Fetching signed URL for:', attachment.storagePath);

      const response = await api.post('/api/files/generate-signed-url', {
        bucket: 'message-attachments',
        path: attachment.storagePath,
        expiresIn: 3600 // 1 hour
      });

      if (!response?.success) {
        console.error('[Attachment] Error creating signed URL:', response?.error);
        throw new Error(response?.error || 'Failed to create signed URL');
      }

      console.log('[Attachment] Signed URL created (cached):', response.data.url);
      return response.data.url;
    },
    staleTime: 50 * 60 * 1000, // 50 minutes (cache as "fresh" for 50 min)
    cacheTime: 55 * 60 * 1000, // 55 minutes (keep in cache for 55 min)
    retry: 2,
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  const isImage = attachment.contentType?.startsWith('image/');
  const isVideo = attachment.contentType?.startsWith('video/');

  // Open the swipeable gallery scoped to this message's media (images + videos)
  const handleOpenGallery = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onOpenGallery && siblingAttachments) {
      onOpenGallery(siblingAttachments, attachment);
    }
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = attachment.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('[Attachment] Error downloading file:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <div className="h-4 w-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
        <span className="text-xs">Loading...</span>
      </div>
    );
  }

  if (!url) return null;

  return (
    <div className="rounded-lg overflow-hidden">
      {isImage ? (
        <div className="relative group">
          <button
            type="button"
            onClick={handleOpenGallery}
            className="block w-full"
          >
            <img
              src={url}
              alt={attachment.fileName}
              className="h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity border border-white/20"
              style={{ maxWidth: '200px', objectFit: 'contain' }}
              onLoad={() => onLoaded?.()}
            />
          </button>
          <button
            onClick={handleDownload}
            className={`absolute top-2 right-2 p-1.5 rounded-lg ${
              isOwn
                ? 'bg-primary-foreground/80 hover:bg-primary-foreground text-primary'
                : 'bg-background/80 hover:bg-background text-foreground'
            } opacity-0 group-hover:opacity-100 transition-opacity shadow-lg`}
            title="Download"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>
      ) : isVideo ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleOpenGallery}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg flex-1 text-left ${
              isOwn
                ? 'bg-primary-foreground/10 hover:bg-primary-foreground/20'
                : 'bg-muted-foreground/10 hover:bg-muted-foreground/20'
            } transition-colors`}
          >
            <File className="h-4 w-4" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">
                {attachment.fileName}
              </p>
              <p className={`text-[10px] ${
                isOwn ? 'text-primary-foreground/60' : 'text-muted-foreground/70'
              }`}>
                {(attachment.fileSize / 1024).toFixed(1)} KB
              </p>
            </div>
          </button>
          <button
            onClick={handleDownload}
            className={`p-2 rounded-lg ${
              isOwn
                ? 'bg-primary-foreground/10 hover:bg-primary-foreground/20'
                : 'bg-muted-foreground/10 hover:bg-muted-foreground/20'
            } transition-colors`}
            title="Download"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-2 px-3 py-2 rounded-lg flex-1 ${
              isOwn
                ? 'bg-primary-foreground/10 hover:bg-primary-foreground/20'
                : 'bg-muted-foreground/10 hover:bg-muted-foreground/20'
            } transition-colors`}
          >
            <File className="h-4 w-4" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">
                {attachment.fileName}
              </p>
              <p className={`text-[10px] ${
                isOwn ? 'text-primary-foreground/60' : 'text-muted-foreground/70'
              }`}>
                {(attachment.fileSize / 1024).toFixed(1)} KB
              </p>
            </div>
          </a>
          <button
            onClick={handleDownload}
            className={`p-2 rounded-lg ${
              isOwn
                ? 'bg-primary-foreground/10 hover:bg-primary-foreground/20'
                : 'bg-muted-foreground/10 hover:bg-muted-foreground/20'
            } transition-colors`}
            title="Download"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

const MessagesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { showSuccess, showError, showInfo } = useToast();
  const { user } = useAuth();
  const { organization } = useOrganization();

  // Get organization admin ID if user is a member
  const orgAdminId = user?.organizationMembership?.role === 'member' && organization ? organization.ownerId : null;

  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | "partners" | "referrals">("all");
  const [expandedReferralIds, setExpandedReferralIds] = useState<Set<number>>(new Set());
  const [expandedNewConvReferralId, setExpandedNewConvReferralId] = useState<number | null>(null);
  const [newConvTab, setNewConvTab] = useState<"partners" | "referrals">("partners");
  const [selectedConversation, setSelectedConversation] = useState<Message | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const MAX_MESSAGE_LENGTH = 5280;
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReportConversation, setShowReportConversation] = useState(false);
  const [referralModalData, setReferralModalData] = useState<any | null>(null);
  const [showProfilePreview, setShowProfilePreview] = useState(false);
  const [profilePreviewPartnerId, setProfilePreviewPartnerId] = useState<string | null>(null);
  // Swipeable attachment gallery (mirrors the referral flow) scoped to one message's media
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryAttachments, setGalleryAttachments] = useState<any[]>([]);
  const [galleryUrls, setGalleryUrls] = useState<Record<number, string>>({});
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [activeMessageMenu, setActiveMessageMenu] = useState<string | null>(null);

  // Fetch full partner profile data when profile preview is opened
  // Uses shared usePublicProfile hook — same data shape /network page renders
  const { data: partnerProfileData, isLoading: isProfileLoading, error: profileError } = usePublicProfile(
    profilePreviewPartnerId,
    showProfilePreview
  );

  // Pull the partner's connection record so the modal can show the favorite
  // button / hamburger menu the same way the Network page does.
  const partnerConnection = usePartnerConnection(profilePreviewPartnerId);

  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [expandedTimeMessageId, setExpandedTimeMessageId] = useState<string | null>(null);
  const [showForwardDialog, setShowForwardDialog] = useState(false);
  const [forwardingMessageId, setForwardingMessageId] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  // Track whether the user is scrolled to (near) the bottom of the thread so a
  // background refresh only auto-scrolls when they're already following along.
  const isAtBottomRef = useRef(true);
  // Distinguish "opened/switched conversation" (jump to bottom) from "new message
  // arrived in the current conversation" (only follow if already at the bottom).
  const prevConversationIdRef = useRef<string | null>(null);
  const prevMessageCountRef = useRef(0);
  // After an open or a followed message we "force stick to bottom" until this
  // timestamp, so images decoding over the next second or two can't strand the
  // view partway up. It's needed because browser scroll-anchoring nudges
  // scrollTop while images load, which fires onScroll and momentarily flips
  // isAtBottomRef off — the ResizeObserver would then refuse to re-pin. A real
  // user scroll (wheel/touch) clears the window immediately (see the handlers).
  const stickToBottomUntilRef = useRef(0);
  // Persist partnerId → referral details so association survives conversation list refetches
  const referralChatMap = useRef<Record<string, {
    referralId: string | number;
    clientName: string;
    jobDescription: string;
    senderName: string;
  }>>({});

  // Check for conversation param in URL
  const conversationParam = searchParams.get("conversation");

  // Fetch conversations
  const { data: conversationsData, isPending } = useQuery({
    queryKey: ["messages", user?.id],
    queryFn: async () => {
      const response = await api.get("/api/workflows/messages/conversations");
      return response.data || []; // Backend returns { success: true, data: [] }
    },
    enabled: !!user,
    refetchOnMount: 'always', // Always refetch when navigating to this page
    refetchOnWindowFocus: true, // Refetch when user returns to the tab
    staleTime: 0, // Always consider data stale
  });

  // Fetch connections for new conversation
  const { data: connectionsData, isPending: isLoadingConnections } = useQuery({
    queryKey: ["/api/workflows/connections"],
    queryFn: async () => {
      const response = await api.get("/api/workflows/connections");
      // The response is already the array due to transformation layer
      return Array.isArray(response) ? response : (response.data || []);
    },
    enabled: showNewConversationModal && !!user,
  });

  // Fetch user's referrals — for the Referrals tab, referral filter, and new conv modal
  const { data: referralsData } = useQuery({
    queryKey: ["referrals", user?.id],
    queryFn: async () => {
      const response = await api.get("/api/workflows/referral/list");
      return Array.isArray(response) ? response : (response.data || []);
    },
    enabled: !!user,
  });

  // Auto-select conversation from URL query parameter (fallback for old-style navigation)
  useEffect(() => {
    const userParam = searchParams.get('user');
    if (userParam && conversationsData && !selectedConversation) {
      // Try to find existing conversation with this user
      let conversation = conversationsData.find(
        (conv) => conv.partnerId === userParam
      );

      // If no existing conversation, we'll need to fetch user details to create placeholder
      // For now, just try to select if exists, otherwise the user will need to use "New Conversation"
      if (conversation) {
        setSelectedConversation(conversation);
        // Clear the query param
        navigate(location.pathname, { replace: true });
      }
      // If no conversation exists, user needs to be fetched - this would require an API call
      // For simplicity, we'll let the user click "New Conversation" for now
    }
  }, [searchParams, conversationsData, selectedConversation, navigate, location.pathname]);

  // Auto-select conversation from notification or create new conversation
  useEffect(() => {
    // Trigger auto-selection if we have either a partnerId OR it's a connection request
    const shouldAutoSelect = (location.state?.autoSelectPartnerId || location.state?.isConnectionRequest)
                             && conversationsData
                             && !selectedConversation;

    if (shouldAutoSelect) {
      // Try to find by partnerId first (for existing conversations)
      let conversation = null;
      if (location.state.autoSelectPartnerId && conversationsData && conversationsData.length > 0) {
        conversation = conversationsData.find(
          (conv) => conv.partnerId === location.state.autoSelectPartnerId
        );
      }

      // Fallback: If this is a connection request but no partnerId match, find the first connection request
      if (!conversation && location.state.isConnectionRequest && conversationsData && conversationsData.length > 0) {
        conversation = conversationsData.find(conv => conv.type === 'connection_request');
      }

      // If no conversation found but we have partner info, create a new conversation placeholder.
      // Use the predicted direct-thread id so the cache reconciles with the
      // server-side conversation as soon as the first message is sent.
      if (!conversation && location.state.newConversationPartner) {
        const partner = location.state.newConversationPartner;
        const partnerName = [partner.firstName, partner.lastName].filter(Boolean).join(' ') || partner.company || 'Unknown';
        const rawChatId = user?.id ? computeChatId(user.id, partner.id) : `new-${partner.id}`;

        conversation = {
          id: rawChatId,
          chatId: rawChatId,
          type: 'conversation' as const,
          partnerId: partner.id,
          partnerName,
          partnerTrade: partner.trade,
          partnerAvatar: partner.profileImageUrl,
          lastMessage: 'Start a new conversation',
          lastMessageTime: new Date().toISOString(),
          unreadCount: 0,
          isNewConversation: true,
          isOrgAdmin: orgAdminId ? partner.id === orgAdminId : false
        };
      }

      if (conversation) {
        setSelectedConversation(conversation);

        // For connection requests, mark as read immediately since we don't fetch messages
        if (conversation.type === 'connection_request') {
          markConversationAsReadMutation.mutate(conversation.id);
        }

        // Clear the navigation state to prevent re-selecting on component updates
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, conversationsData, selectedConversation, navigate, location.pathname, orgAdminId, user?.id]);

  // Normalise conversations — add isOrgAdmin flag, surface the raw chatId for
  // realtime filtering, and reconcile referralId across naming conventions.
  const conversations = useMemo(() => {
    if (!conversationsData) return [];
    return conversationsData.map((conv: any) => {
      const rawChatId: string =
        conv.chatId ?? conv.chat_id ?? extractRawChatId(conv.id ?? "");
      return {
        ...conv,
        chatId: rawChatId,
        isOrgAdmin: orgAdminId ? conv.partnerId === orgAdminId : false,
        // Handle both camelCase (frontend) and snake_case (backend) referral id.
        // Fall back to the local referralChatMap which persists the association even after refetches.
        referralId:
          conv.referralId ??
          conv.referral_id ??
          referralChatMap.current[conv.partnerId]?.referralId ??
          undefined,
      };
    });
  }, [conversationsData, orgAdminId]);

  // Fetch chat messages for selected conversation
  const queryEnabled = !!selectedConversation && selectedConversation.type === "conversation";
  const chatIdRef = useRef<string | null>(null);

  const { data: chatMessages, isFetching: isFetchingMessages } = useQuery({
    queryKey: ["chat", selectedConversation?.id],
    queryFn: async () => {
      if (!selectedConversation?.id) return [];
      const response = await api.get(`/api/workflows/messages/${selectedConversation.id}`);
      return response.data || [];
    },
    enabled: queryEnabled,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false, // Don't refetch on window focus - we have realtime updates
    refetchOnReconnect: false, // Don't refetch on reconnect - realtime will catch up
    // Poll while a conversation is open as a fallback for the Supabase realtime
    // push. Realtime is the instant path, but if a postgres_changes event is
    // missed (auth/RLS hiccup, dropped socket, or the tab was already mounted so
    // refetchOnMount never fires), this guarantees new messages appear within a
    // few seconds instead of only after a manual page refresh. React Query pauses
    // this interval automatically when the tab is not focused.
    refetchInterval: queryEnabled ? 5000 : false,
  });

  // Fetch attachments for every message in the conversation.
  //
  // NOTE: the /api/message-attachments endpoint ignores its messageId filter and
  // always returns the full attachment set, so the previous implementation fired
  // one request PER message — N identical full-table fetches per refetch. That
  // storm made a received image slow/unreliable to appear (and got worse with
  // retries). We now make a SINGLE request and bucket the results by message id
  // on the client, keeping only this conversation's messages. One cheap request
  // means the refetch that surfaces a just-received image is fast and reliable.
  const { data: messageAttachments } = useQuery({
    queryKey: ["attachments", selectedConversation?.id, chatMessages?.length],
    queryFn: async () => {
      if (!chatMessages || chatMessages.length === 0) return {};

      const messageIds = new Set(chatMessages.map((msg: any) => msg.id));

      const response = await api.get(`/api/message-attachments?limit=1000`);
      const attachments = Array.isArray(response) ? response : (response.data || []);

      const attachmentsByMessage: { [key: string]: any[] } = {};
      for (const att of attachments) {
        const messageId = att.messageId || att.message_id;
        // Only keep attachments belonging to a message in THIS conversation.
        if (!messageIds.has(messageId)) continue;

        const normalized = {
          ...att,
          messageId,
          fileName: att.fileName || att.file_name,
          fileSize: att.fileSize || att.file_size,
          contentType: att.contentType || att.content_type,
          storagePath: att.storagePath || att.storage_path,
          storageBucket: att.storageBucket || att.storage_bucket,
          thumbnailPath: att.thumbnailPath || att.thumbnail_path,
          createdAt: att.createdAt || att.created_at,
        };
        (attachmentsByMessage[messageId] ||= []).push(normalized);
      }

      return attachmentsByMessage;
    },
    enabled: queryEnabled && !!chatMessages && chatMessages.length > 0,
    // The sender writes the message row FIRST and uploads the file afterwards
    // (see handleSendMessage), so a received image's attachment row can land
    // many seconds after the message that carries it — a mobile sender may spend
    // 10s+ compressing a camera photo and pushing it over a slow uplink. With a
    // long staleTime and no interval, this query fetched once when the message
    // count changed, found no attachment row yet, cached the empty result and
    // never looked again: the receiver saw the text but never the image (and the
    // NEXT message would reveal the previous one, because a new count means a
    // new key means one more fetch). Poll on the same cadence as the message
    // query instead, so a late-arriving attachment always surfaces within a few
    // seconds regardless of how slow the upload was or whether realtime fired.
    // structuralSharing keeps the identity of unchanged entries, so a poll that
    // returns the same map re-renders nothing and never reloads a visible image.
    staleTime: 0,
    refetchInterval: queryEnabled ? 5000 : false,
    // The query key includes chatMessages.length, so every new message changes
    // the key. Without keeping the previous data, the attachment map blanks to
    // undefined during the refetch, which collapses every already-rendered image
    // to zero height and then re-expands it when the fetch returns — yanking the
    // scroll position upward right after a new message lands. Keeping previous
    // data holds the images in place so the thread stays put at the bottom.
    placeholderData: keepPreviousData,
  });

  // Real-time subscription for new messages in the active conversation
  useEffect(() => {
    if (!selectedConversation || selectedConversation.type !== "conversation" || !user) {
      return;
    }

    // The conversation `id` is composite (e.g. `chatA::ref-7`), but the
    // realtime layer can only filter on the raw `chat_id` column. We use the
    // raw chatId for the filter and re-check referral_id in the handlers so
    // referral-thread messages don't leak into a direct thread (and vice versa).
    const conversationId = selectedConversation.id;
    const rawChatId =
      selectedConversation.chatId ?? extractRawChatId(conversationId);
    const conversationReferralId =
      selectedConversation.referralId !== undefined &&
      selectedConversation.referralId !== null
        ? Number(selectedConversation.referralId)
        : null;
    chatIdRef.current = rawChatId;

    const matchesThisThread = (
      payload: { new?: any; old?: any } | undefined,
    ): boolean => {
      const row = (payload?.new || payload?.old) as { referral_id?: number | null } | undefined;
      if (!row) return false;
      const rowReferralId =
        row.referral_id !== undefined && row.referral_id !== null
          ? Number(row.referral_id)
          : null;
      return rowReferralId === conversationReferralId;
    };

    console.log('[Messages] Setting up real-time subscription for chat:', {
      conversationId,
      rawChatId,
      referralId: conversationReferralId,
    });
    console.log('[Messages] User ID:', user.id);
    console.log('[Messages] Selected conversation:', selectedConversation);

    // Subscribe to new messages and updates in this conversation
    // IMPORTANT: Use 'messages-' prefix to match the existing channel from useRealtimeMessages
    let subscription: any;
    const setupSubscription = async () => {
      subscription = supabase
      .channel(`messages-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_messages'
        },
        (payload) => {
          console.log('[Messages] ⚡ CATCH-ALL EVENT for chat_messages:', {
            event: payload.eventType,
            chatId: payload.new?.chat_id || payload.old?.chat_id,
            messageId: payload.new?.id || payload.old?.id,
            filter: `chat_id=eq.${rawChatId}`,
            referralIdInPayload:
              (payload.new as any)?.referral_id ??
              (payload.old as any)?.referral_id ??
              null,
            matches: (payload.new?.chat_id || payload.old?.chat_id) === rawChatId
          });

          // Hard message deletes ("delete for everyone") arrive as DELETE events.
          // Supabase only ships the primary key on DELETE (default replica
          // identity), so we can't rely on the chat_id filter — instead remove
          // the row from the open thread's cache if it's present there. Matching
          // on an id already in this thread guarantees no cross-thread bleed.
          if (payload.eventType === 'DELETE') {
            const deletedId = (payload.old as any)?.id;
            if (!deletedId) return;
            queryClient.setQueryData(
              ["chat", conversationId],
              (oldMessages: any[] | undefined) => {
                if (!oldMessages) return oldMessages;
                if (!oldMessages.some(msg => msg.id === deletedId)) return oldMessages;
                return oldMessages.filter(msg => msg.id !== deletedId);
              }
            );
            queryClient.invalidateQueries({
              queryKey: ["messages", user.id],
              refetchType: 'none'
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `chat_id=eq.${rawChatId}`
        },
        async (payload) => {
          try {
            console.log('[Messages] New message received via real-time:', payload);

            // Discard messages belonging to the *other* thread on this chat_id
            // (i.e. a referral message arriving while a direct thread is open).
            if (!matchesThisThread(payload)) {
              console.log('[Messages] Ignoring INSERT for different thread', {
                payloadReferralId: payload.new?.referral_id,
                conversationReferralId,
              });
              return;
            }

            const isOwnMessage = payload.new.sender_id === user.id;

            // Add the new message to the query cache
            queryClient.setQueryData(
              ["chat", conversationId],
              (oldMessages: any[] | undefined) => {
                const newMessage = {
                  id: payload.new.id,
                  senderId: payload.new.sender_id,
                  senderName: isOwnMessage ? undefined : selectedConversation?.partnerName,
                  content: payload.new.message_content,
                  timestamp: payload.new.created_at,
                  isOwn: isOwnMessage,
                  editedAt: payload.new.edited_at || null,
                  messageType: payload.new.message_type || 'user',
                  reactions: []
                };

                // Avoid duplicates
                if (oldMessages?.some(msg => msg.id === newMessage.id)) {
                  console.log('[Messages] Duplicate message detected, skipping:', newMessage.id);
                  return oldMessages;
                }

                console.log('[Messages] Adding new message to cache:', newMessage);
                return [...(oldMessages || []), newMessage];
              }
            );

            // If this is a message from someone else (not our own), mark the conversation as read
            // since we're actively viewing it. We pass the composite id so the
            // backend only marks messages in *this* thread as read.
            if (!isOwnMessage) {
              console.log('[Messages] Marking conversation as read:', conversationId);
              try {
                await api.patch(`/api/workflows/messages/${conversationId}/mark-read`);
                console.log('[Messages] Conversation marked as read');

                // Invalidate unread counts
                queryClient.invalidateQueries({
                  queryKey: ["unreadMessages"],
                  refetchType: 'active'
                });
              } catch (error) {
                console.error('[Messages] Failed to mark conversation as read:', error);
              }
            }

            // Also invalidate the conversations list to update last message
            queryClient.invalidateQueries({
              queryKey: ["messages", user.id],
              refetchType: 'none' // Don't refetch, just mark as stale
            });

            // Pull the attachment map right away so an image whose upload had
            // already finished by the time this INSERT arrived shows up without
            // waiting for the next poll. A fixed retry ladder used to live here
            // instead, but it gave up after a few seconds — a slow mobile upload
            // outlasted it and the image then never appeared. The attachments
            // query now polls, so late uploads are covered without guessing at a
            // deadline. refetchType 'active' makes this a no-op once the
            // conversation is closed.
            if (!isOwnMessage) {
              queryClient.invalidateQueries({
                queryKey: ["attachments", conversationId],
                refetchType: 'active',
              });
            }
          } catch (error) {
            console.error('[Messages] Error in INSERT handler:', error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
          filter: `chat_id=eq.${rawChatId}`
        },
        (payload) => {
          try {
            console.log('[Messages] Message updated via real-time:', payload);

            // Skip updates that belong to a different thread on the same chat_id.
            if (!matchesThisThread(payload)) {
              console.log('[Messages] Ignoring UPDATE for different thread', {
                payloadReferralId: payload.new?.referral_id,
                conversationReferralId,
              });
              return;
            }

            const isOwnMessage = payload.new.sender_id === user.id;

            // Update the message in cache (for edits)
            // If message doesn't exist, add it (handles case where UPDATE comes before INSERT)
            queryClient.setQueryData(
              ["chat", conversationId],
              (oldMessages: any[] | undefined) => {
                if (!oldMessages) return oldMessages;

                const messageExists = oldMessages.some(msg => msg.id === payload.new.id);

                if (messageExists) {
                  // Update existing message
                  console.log('[Messages] Updating existing message in cache:', payload.new.id);
                  return oldMessages.map(msg =>
                    msg.id === payload.new.id
                      ? {
                          ...msg,
                          content: payload.new.message_content,
                          editedAt: payload.new.edited_at || null
                        }
                      : msg
                  );
                } else {
                  // Message doesn't exist yet, add it
                  console.log('[Messages] UPDATE event for new message - adding to cache');
                  const newMessage = {
                    id: payload.new.id,
                    senderId: payload.new.sender_id,
                    senderName: isOwnMessage ? undefined : selectedConversation?.partnerName,
                    content: payload.new.message_content,
                    timestamp: payload.new.created_at,
                    isOwn: isOwnMessage,
                    editedAt: payload.new.edited_at || null,
                    messageType: payload.new.message_type || 'user',
                    reactions: []
                  };
                  return [...oldMessages, newMessage];
                }
              }
            );

            // Also invalidate conversations list to update last message
            queryClient.invalidateQueries({
              queryKey: ["messages", user.id],
              refetchType: 'none'
            });
          } catch (error) {
            console.error('[Messages] Error in UPDATE handler:', error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_reactions'
        },
        (payload) => {
          console.log('[Messages] Reaction change via real-time:', payload);
          // Refetch messages to get updated reactions
          queryClient.invalidateQueries({
            queryKey: ["chat", conversationId],
            refetchType: 'active'
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_attachments'
        },
        (payload) => {
          console.log('[Messages] Attachment change via real-time:', payload);
          // Refresh the ATTACHMENT map, not the message list. The attachments
          // live in their own query, so invalidating ["chat"] here refetched the
          // messages (whose count is unchanged by an upload) and left the
          // attachment map untouched — the one event that should have shown a
          // received image instantly was refreshing the wrong cache. This is the
          // instant path for a late-uploaded image; the poll above is the
          // fallback for when realtime doesn't deliver.
          queryClient.invalidateQueries({
            queryKey: ["attachments", conversationId],
            refetchType: 'active'
          });
        }
      )
      .subscribe((status) => {
        console.log('[Messages] Subscription status:', status);
      });
    };
    setupSubscription();

    // Cleanup subscription when conversation changes or component unmounts
    return () => {
      console.log('[Messages] Cleaning up real-time subscription for chat:', conversationId);
      if (subscription) subscription.unsubscribe();
    };
  }, [selectedConversation?.id, selectedConversation?.chatId, selectedConversation?.referralId, selectedConversation?.type, user, queryClient]);

  // Invalidate unread counts immediately when auto-selecting from notification
  useEffect(() => {
    if (location.state?.autoSelectPartnerId && selectedConversation) {
      queryClient.invalidateQueries({
        queryKey: ["unreadMessages"],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ["messages", user?.id],
        refetchType: 'active'
      });
    }
  }, [location.state?.autoSelectPartnerId, selectedConversation, queryClient, user?.id]);

  // Invalidate unread counts when messages are successfully fetched
  useEffect(() => {
    if (chatMessages && !isFetchingMessages && selectedConversation) {
      queryClient.invalidateQueries({
        queryKey: ["unreadMessages"],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ["messages", user?.id],
        refetchType: 'active'
      });
    }
  }, [chatMessages, isFetchingMessages, selectedConversation, queryClient, user?.id]);

  // Auto-grow the message composer with its content so long messages are fully
  // visible before sending. CSS max-height clamps it and scroll takes over past
  // the cap — 'auto' first so the textarea can also shrink after a send.
  useEffect(() => {
    const ta = messageInputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }, [newMessage]);

  // Keep the messages container scrolled sensibly WITHOUT ever yanking the user
  // away from where they're reading. Two distinct cases:
  //   1. A conversation is opened/switched → jump straight to the latest message.
  //   2. New message(s) arrive in the open conversation (send, realtime push, or
  //      the 5s poll fallback) → only follow to the bottom if the user was already
  //      at the bottom. If they've scrolled up to read history, the browser keeps
  //      their position naturally (content is appended below the viewport), so we
  //      leave the scroll untouched.
  useEffect(() => {
    const count = chatMessages?.length ?? 0;
    const conversationId = selectedConversation?.id ?? null;
    const conversationChanged = prevConversationIdRef.current !== conversationId;

    if (conversationChanged) {
      // New conversation opened/switched: follow it from the bottom until the
      // user scrolls up. Set this even when messages are still loading (count 0)
      // so that when they arrive we correctly treat the load as "already at the
      // bottom" instead of leaving a stale not-at-bottom flag from the previous
      // thread. The actual pinning is handled here and by the ResizeObserver as
      // content (and images) finish laying out.
      isAtBottomRef.current = true;
      stickToBottomUntilRef.current = Date.now() + 2000;
      prevConversationIdRef.current = conversationId;
      prevMessageCountRef.current = count;
      if (count > 0) {
        setTimeout(() => {
          const el = messagesContainerRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        }, 100);
      }
      return;
    }

    if (count > prevMessageCountRef.current && isAtBottomRef.current) {
      // Also force-stick briefly so a newly-arrived image (which loads after the
      // message renders) pins to the bottom once it decodes.
      stickToBottomUntilRef.current = Date.now() + 2000;
      // New message while already following the thread — pin to the bottom.
      // Instant (not smooth) on purpose: a smooth animation fires intermediate
      // onScroll events that momentarily read as "not at bottom" and flip
      // isAtBottomRef off, so a quick follow-up message would then fail to
      // follow. Scroll the container itself (not scrollIntoView, which would
      // also move the page) so nothing outside the thread ever moves.
      setTimeout(() => {
        const el = messagesContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      }, 100);
    }

    prevMessageCountRef.current = count;
  }, [chatMessages?.length, selectedConversation?.id]);

  // Record whether the user is at (or near) the bottom of the thread on every
  // scroll, so the effect above knows whether an incoming message should follow.
  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    // 80px of slack so "almost at the bottom" still counts as following along.
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 80;
  };

  // A just-sent/received image loads asynchronously and grows its bubble after we
  // already scrolled to the bottom, leaving the last image partly below the fold.
  // Re-pin to the bottom once it loads — but only if the user is still following
  // the thread, so we never yank someone who has scrolled up to read history.
  const handleMediaLoaded = () => {
    if (!isAtBottomRef.current) return;
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  // Robust "stay at bottom": watch the thread content for ANY height change —
  // messages rendering on open, images decoding (even from cache, where onLoad
  // never fires), attachments arriving — and re-pin to the bottom whenever the
  // user is still following. A one-shot scroll on open can't do this because a
  // long thread's images finish laying out well after the timer runs, leaving it
  // stranded partway up. Only pins while isAtBottomRef is true, so a user reading
  // history is never yanked.
  useEffect(() => {
    const content = messagesContentRef.current;
    const container = messagesContainerRef.current;
    if (!content || !container) return;
    const observer = new ResizeObserver(() => {
      // Pin when the user is following the thread, OR during the brief
      // force-stick window after an open/received message (covers images that
      // decode late and would otherwise leave the view stranded above the fold).
      if (isAtBottomRef.current || Date.now() < stickToBottomUntilRef.current) {
        container.scrollTop = container.scrollHeight;
      }
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [selectedConversation?.id]);

  // A genuine user scroll (wheel or touch drag) cancels the force-stick window
  // immediately, so we never fight someone who wants to scroll up to read
  // history right after opening a conversation.
  const handleUserScrollIntent = () => {
    stickToBottomUntilRef.current = 0;
  };

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (data: { recipientId: string; content: string; referralId?: number }) => {
      return api.post("/api/workflows/messages/send", {
        recipientId: data.recipientId,
        messageContent: data.content,
        messageType: "text",
        referralId: data.referralId,
      });
    },
    onMutate: async (variables) => {
      if (!selectedConversation) return;

      // Sending your own message always snaps the thread to the bottom, even if
      // you'd scrolled up — mark yourself as "at the bottom" so the scroll effect
      // follows the optimistic message down.
      isAtBottomRef.current = true;

      // The placeholder conversation id now matches the real server-side
      // conversation id (chat_id, optionally suffixed with `::ref-N`), so we
      // can run the optimistic update for *new* conversations too — that's
      // what makes the just-sent message appear immediately rather than
      // disappearing while the conversations list refetches.
      await queryClient.cancelQueries({ queryKey: ["chat", selectedConversation.id] });

      // Snapshot current messages for rollback
      const previousMessages = queryClient.getQueryData(["chat", selectedConversation.id]);

      // Immediately append the optimistic message
      const optimisticMsg = {
        id: `optimistic-${Date.now()}`,
        senderId: user?.id,
        senderName: undefined,
        content: variables.content,
        timestamp: new Date().toISOString(),
        isOwn: true,
        editedAt: null,
        reactions: [],
      };

      queryClient.setQueryData(["chat", selectedConversation.id], (old: any) => {
        return Array.isArray(old) ? [...old, optimisticMsg] : [optimisticMsg];
      });

      // Clear the input immediately
      setNewMessage("");

      return { previousMessages };
    },
    onSuccess: async (_data, _variables) => {
      const wasNewConversation = selectedConversation?.isNewConversation;
      if (wasNewConversation) {
        setNewMessage("");
        // Flip the placeholder flag — the conversation now exists server-side.
        // The id already matches the canonical id so no swap-out is needed.
        setSelectedConversation((prev) =>
          prev ? { ...prev, isNewConversation: false } : prev,
        );
      }

      // Refetch to replace the optimistic message with the real server data.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["chat", selectedConversation?.id],
          refetchType: 'active'
        }),
        queryClient.invalidateQueries({
          queryKey: ["messages", user?.id],
          refetchType: 'active'
        })
      ]);
    },
    onError: (_err, _variables, context: any) => {
      // Roll back the optimistic update
      if (context?.previousMessages !== undefined) {
        queryClient.setQueryData(["chat", selectedConversation?.id], context.previousMessages);
      }
      showError("Send Failed", "Could not send message");
    },
  });

  // Accept connection request
  const acceptConnectionMutation = useMutation({
    mutationFn: async (partnerId: string) => {
      return api.post("/api/workflows/connections/accept", { requesterId: partnerId });
    },
    onSuccess: () => {
      showSuccess("Connected!", "You are now connected as partners");
      queryClient.invalidateQueries({
        queryKey: ["messages", user?.id],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ["unreadMessages"],
        refetchType: 'active'
      });
      setSelectedConversation(null);
    },
    onError: () => {
      showError("Error", "Failed to accept connection");
    },
  });

  // Decline connection request
  const declineConnectionMutation = useMutation({
    mutationFn: async (partnerId: string) => {
      return api.post("/api/workflows/connections/decline", { requesterId: partnerId });
    },
    onSuccess: () => {
      showSuccess("Declined", "Connection request declined");
      queryClient.invalidateQueries({
        queryKey: ["messages", user?.id],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ["unreadMessages"],
        refetchType: 'active'
      });
      setSelectedConversation(null);
    },
    onError: () => {
      showError("Error", "Failed to decline connection");
    },
  });

  // Mark conversation as read (for connection requests that don't fetch messages)
  const markConversationAsReadMutation = useMutation({
    mutationFn: async (chatId: string) => {
      return api.patch(`/api/workflows/messages/${chatId}/mark-read`);
    },
    onSuccess: () => {
      // Invalidate unread counts and force immediate refetch
      queryClient.invalidateQueries({
        queryKey: ["unreadMessages"],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ["messages", user?.id],
        refetchType: 'active'
      });
    },
  });

  // Delete conversation mutation
  const deleteConversationMutation = useMutation({
    mutationFn: async (chatId: string) => {
      return api.delete(`/api/workflows/messages/${chatId}`);
    },
    onSuccess: () => {
      showSuccess("Conversation deleted");
      setSelectedConversation(null);
      setShowDeleteConfirm(false);
      queryClient.invalidateQueries({ queryKey: ["messages", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["unreadMessages"] });
    },
    onError: () => {
      showError("Failed to delete conversation");
    },
  });

  // Edit message mutation
  const editMessageMutation = useMutation({
    mutationFn: async (data: { messageId: string; messageContent: string }) => {
      return api.put(`/api/workflows/messages/${data.messageId}/edit`, {
        messageContent: data.messageContent,
      });
    },
    onSuccess: (_data, variables) => {
      setEditingMessageId(null);
      setEditContent("");
      // Update the message in cache
      queryClient.setQueryData(
        ["chat", selectedConversation?.id],
        (oldMessages: ChatMessage[] | undefined) => {
          if (!oldMessages) return oldMessages;
          return oldMessages.map((msg) =>
            msg.id === variables.messageId
              ? { ...msg, content: variables.messageContent, editedAt: new Date().toISOString() }
              : msg
          );
        }
      );
    },
    onError: () => {
      showError("Failed to edit message");
    },
  });

  // Delete message mutation (sender-only; hard delete for everyone + R2 cleanup)
  const deleteMessageMutation = useMutation({
    mutationFn: async (messageId: string) => {
      return api.delete(`/api/workflows/messages/single/${messageId}`);
    },
    onSuccess: (_data, messageId) => {
      // No success toast: the message vanishing from the thread is its own confirmation.
      // Remove the message from this client's cache; the other participant's
      // client removes it via the realtime DELETE event.
      queryClient.setQueryData(
        ["chat", selectedConversation?.id],
        (oldMessages: ChatMessage[] | undefined) => {
          if (!oldMessages) return oldMessages;
          return oldMessages.filter((msg) => msg.id !== messageId);
        }
      );
      // Also invalidate conversations to update last message
      queryClient.invalidateQueries({ queryKey: ["messages", user?.id] });
    },
    onError: () => {
      showError("Failed to delete message");
    },
  });

  // Forward message mutation
  const forwardMessageMutation = useMutation({
    mutationFn: async (data: { recipientId: string; originalMessageId: string }) => {
      return api.post("/api/workflows/messages/forward", {
        recipientId: data.recipientId,
        originalMessageId: data.originalMessageId,
      });
    },
    onSuccess: () => {
      showSuccess("Message forwarded");
      setShowForwardDialog(false);
      setForwardingMessageId(null);
      queryClient.invalidateQueries({ queryKey: ["messages", user?.id] });
    },
    onError: () => {
      showError("Failed to forward message");
    },
  });

  // Toggle reaction mutation
  const toggleReactionMutation = useMutation({
    mutationFn: async (data: { messageId: string; emoji: string }) => {
      return api.post(`/api/workflows/messages/${data.messageId}/reactions`, {
        emoji: data.emoji,
      });
    },
    onSuccess: (response: any, variables) => {
      // Update reactions in cache
      queryClient.setQueryData(
        ["chat", selectedConversation?.id],
        (oldMessages: ChatMessage[] | undefined) => {
          if (!oldMessages) return oldMessages;
          return oldMessages.map((msg) => {
            if (msg.id !== variables.messageId) return msg;
            const currentReactions = msg.reactions || [];
            const action = response?.data?.action || response?.action;
            if (action === "removed") {
              return {
                ...msg,
                reactions: currentReactions.filter(
                  (r) => !(r.emoji === variables.emoji && r.userId === user?.id)
                ),
              };
            }
            return {
              ...msg,
              reactions: [
                ...currentReactions,
                {
                  emoji: variables.emoji,
                  userId: user?.id || "",
                  userName: user?.fullName || user?.firstName || "You",
                  id: response?.data?.data?.id || ""
                },
              ],
            };
          });
        }
      );
      setActiveMessageMenu(null);
    },
    onError: () => {
      showError("Failed to react");
    },
  });

  // Fetch connections for forward dialog
  const { data: forwardConnectionsData } = useQuery({
    queryKey: ["/api/workflows/connections", "forward"],
    queryFn: async () => {
      const response = await api.get("/api/workflows/connections");
      return Array.isArray(response) ? response : (response.data || []);
    },
    enabled: showForwardDialog && !!user,
  });

  // Filter conversations
  const filteredConversations = useMemo(() => {
    return conversations.filter((conv) => {
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        if (
          !conv.partnerName.toLowerCase().includes(search) &&
          !conv.lastMessage.toLowerCase().includes(search)
        ) return false;
      }
      if (activeTab === "partners") return (conv.type === "conversation" || conv.type === "connection_request") && !conv.referralId;
      if (activeTab === "referrals") return !!conv.referralId;
      return true;
    });
  }, [conversations, searchTerm, activeTab]);

  // Start a chat about a specific referral.
  // Looks up the *referral* thread for (me, partnerId, referralId) and reuses it
  // if it exists; otherwise builds a placeholder whose id matches the eventual
  // server-side composite id, so the React Query cache stays consistent across
  // the first send.
  const handleStartReferralChat = (referral: any, partnerId: string, partnerName: string, partnerTrade?: string) => {
    const existingConv = conversations.find(
      (c) =>
        c.partnerId === partnerId &&
        c.referralId !== undefined &&
        c.referralId !== null &&
        Number(c.referralId) === Number(referral.id),
    );
    const rawChatId = user?.id ? computeChatId(user.id, partnerId) : "";
    const composedId = buildConversationId(rawChatId, referral.id);

    const base: Message = existingConv ?? {
      id: composedId,
      chatId: rawChatId,
      type: "conversation",
      partnerId,
      partnerName,
      partnerTrade,
      lastMessage: "Start a new conversation",
      lastMessageTime: new Date().toISOString(),
      unreadCount: 0,
      isNewConversation: true,
    };
    // Persist the mapping so the referral details survive conversation list refetches
    referralChatMap.current[partnerId] = {
      referralId: referral.id,
      clientName: referral.clientName || referral.customerName || 'Contact',
      jobDescription: referral.jobDescription || '',
      senderName: referral.senderName || '',
    };
    // Always stamp referralId so the conversation appears in the Referrals tab
    const conv: Message = { ...base, referralId: referral.id };
    setSelectedConversation(conv);
    setShowNewConversationModal(false);
    setExpandedNewConvReferralId(null);
  };

  // Toggle a referral row in the Referrals tab
  const toggleReferralExpand = (id: number) => {
    setExpandedReferralIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Format relative time
  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "now";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString();
  };

  // Format time as HH:MM AM/PM
  const formatMessageTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  // Format date for separator
  const formatDateSeparator = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: "long" });
    }
    return date.toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" });
  };

  // Group messages by date for separators
  const getMessageDateKey = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await processSelectedFiles(Array.from(e.target.files || []));
  };

  // Shared by the web file input and the native camera — both hand over plain Files.
  const processSelectedFiles = async (files: File[]) => {
    if (files.length > 0) {
      // Per-MIME size caps must mirror the backend (storage.service.ts):
      // images → 25MB; documents → 100MB; videos → 250MB. Keep these constants
      // in lock-step with the per-type caps enforced server-side
      // (maxUploadSizeForContentType in storage.service.ts).
      const MAX_IMAGE_SIZE = 25 * 1024 * 1024; // 25MB (images)
      const MAX_DOCUMENT_SIZE = 100 * 1024 * 1024; // 100MB (documents)
      const MAX_VIDEO_SIZE = 250 * 1024 * 1024; // 250MB (videos, incl. HEVC/.mov)
      // Only images *over* the cap are compressed, and only far enough to fit
      // it. Anything at or under 25MB is uploaded untouched — compressing it
      // would trade image quality for storage we're happy to spend.
      const COMPRESSION_TARGET_MB = 24; // Just under MAX_IMAGE_SIZE, leaving margin
      const validFiles: File[] = [];
      const errors: string[] = [];

      for (const rawFile of files) {
        // Normalize media files first — some browsers report empty / wrong
        // MIME types for HEVC / .mov / .heic; the helper falls back to the
        // file extension and re-wraps the File with a canonical MIME so the
        // multipart upload sends a Content-Type the server allow-lists.
        const classified = classifyMediaFile(rawFile);
        const file = classified.file;
        const isVideo = classified.kind === 'video';
        const isImage = classified.kind === 'image';

        // Non-media types still need an extension/MIME allow-list (PDFs,
        // Office docs, archives). Media is handled above by classification.
        const documentMimeTypes = [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'text/plain',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/zip', 'application/x-zip-compressed',
          'application/gzip', 'application/x-gzip',
          'application/x-tar', 'application/x-compressed-tar',
          'application/x-7z-compressed'
        ];
        const documentExtensions = [
          '.pdf', '.doc', '.docx', '.txt', '.xls', '.xlsx',
          '.zip', '.gz', '.tar', '.tar.gz', '.tgz', '.7z'
        ];

        const fileName = file.name.toLowerCase();
        const isDocument =
          documentMimeTypes.includes(file.type) ||
          documentExtensions.some(ext => fileName.endsWith(ext));

        if (!isVideo && !isImage && !isDocument) {
          errors.push(
            `"${file.name}" is not a supported file type. Please use images, videos, PDFs, documents, or archives.`
          );
          continue;
        }

        const sizeLimit = isVideo ? MAX_VIDEO_SIZE : isDocument ? MAX_DOCUMENT_SIZE : MAX_IMAGE_SIZE;
        const sizeLimitLabel = isVideo ? '250MB' : isDocument ? '100MB' : '25MB';

        // Handle oversize files. Images can be auto-compressed; videos and
        // documents fail fast with a clear size message because we can't
        // safely transcode them in-browser.
        if (file.size > sizeLimit) {
          const isCompressible = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type);

          if (isImage && isCompressible) {
            try {
              showInfo('Compressing image...', `"${file.name}" is being compressed to fit the ${sizeLimitLabel} limit.`);

              const { compressImage } = await import('@/lib/imageUtils');
              const compressedFile = await compressImage(file, COMPRESSION_TARGET_MB);

              if (compressedFile.size <= MAX_IMAGE_SIZE) {
                validFiles.push(compressedFile);
                continue;
              } else {
                errors.push(`"${file.name}" is too large even after compression (${(compressedFile.size / 1024 / 1024).toFixed(1)}MB). Please resize the image or use a lower quality.`);
                continue;
              }
            } catch (error) {
              console.error('[FileSelect] Compression failed:', error);
              errors.push(`Failed to compress "${file.name}". Please manually reduce the file size.`);
              continue;
            }
          } else if (isVideo) {
            const sizeMB = (file.size / 1024 / 1024).toFixed(1);
            errors.push(`"${file.name}" is too large (${sizeMB}MB). Videos must be ${sizeLimitLabel} or smaller — try trimming the clip or lowering the resolution.`);
            continue;
          } else {
            const sizeMB = (file.size / 1024 / 1024).toFixed(1);
            errors.push(`"${file.name}" is too large (${sizeMB}MB). Maximum file size is ${sizeLimitLabel}. Consider compressing it into a .zip file.`);
            continue;
          }
        }

        validFiles.push(file);
      }

      // Show errors for invalid files
      if (errors.length > 0) {
        errors.forEach(error => showError(error));
      }

      // Add valid files to the list
      if (validFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...validFiles]);
      }
    }

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove selected file
  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload file via BACKEND (not direct to Supabase)
  const uploadFile = async (file: File, messageId: string): Promise<{ success: boolean; attachment?: any; error?: string }> => {
    try {
      console.log('[Upload] ✨✨✨ NEW CODE LOADED - USING BACKEND UPLOAD ✨✨✨');
      console.log('[Upload] Starting upload for file:', file.name, 'messageId:', messageId);

      // Get file metadata (image dimensions if applicable)
      const dimensions = file.type.startsWith('image/') ? await getImageDimensions(file) : null;
      console.log('[Upload] Image dimensions:', dimensions);

      // Create FormData for multipart upload to backend
      const formData = new FormData();
      formData.append('file', file);
      formData.append('messageId', messageId);
      if (dimensions?.width) {
        formData.append('width', dimensions.width.toString());
      }
      if (dimensions?.height) {
        formData.append('height', dimensions.height.toString());
      }

      console.log('[Upload] Sending file to backend endpoint /api/files/message-attachment');

      // Send to backend - it handles BOTH storage upload AND database record creation
      // Backend uses supabaseAdmin which bypasses RLS policies
      // NOTE: Don't set Content-Type manually - the browser sets it with the boundary automatically
      const response = await api.post('/api/files/message-attachment', formData);

      console.log('[Upload] Backend upload complete:', response.data);

      return { success: true, attachment: response.data.data };
    } catch (error: any) {
      console.error('[Upload] Upload error:', error);
      // `error` here is an ApiError from lib/api.ts: it carries the HTTP
      // status, the backend's structured `code`, and a `details` payload
      // (e.g. maxSize for FILE_TOO_LARGE). Hand it to describeUploadError
      // so the user sees a specific message instead of generic "failed".
      const apiErr = error instanceof ApiError ? error : undefined;
      const userMessage = describeUploadError(apiErr ?? { message: error?.message }, file.name);
      console.error('[Upload] Error details:', {
        message: error.message,
        status: apiErr?.status,
        code: apiErr?.data?.code,
        details: apiErr?.data?.details
      });
      return { success: false, error: userMessage };
    }
  };

  // Get image dimensions
  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        resolve({ width: 0, height: 0 });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Get signed URL for attachment (bucket is private, not public)
  // Backend handles this using supabaseAdmin to bypass RLS
  const getAttachmentUrl = async (storagePath: string): Promise<string> => {
    try {
      const response = await api.post('/api/files/generate-signed-url', {
        bucket: 'message-attachments',
        path: storagePath,
        expiresIn: 3600 // 1 hour
      });

      if (!response.data?.success) {
        console.error('Error creating signed URL:', response.data?.error);
        return '';
      }

      return response.data.data.url;
    } catch (error: any) {
      console.error('Error creating signed URL:', error);
      return '';
    }
  };

  // Open the swipeable gallery for a message's media (images + videos), starting
  // at the tapped attachment. Signed URLs are resolved through the same React
  // Query cache AttachmentDisplay uses, so thumbnails and gallery stay in sync.
  const openImageGallery = async (messageAttachments: any[], tapped: any) => {
    const media = (messageAttachments || []).filter((a: any) => {
      const ct = a.contentType || '';
      return ct.startsWith('image/') || ct.startsWith('video/');
    });
    if (media.length === 0) return;

    const startIndex = Math.max(0, media.findIndex((a: any) => a.id === tapped.id));

    // Map to the AttachmentViewerModal attachment shape
    const viewerAttachments = media.map((a: any) => ({
      path: a.storagePath,
      name: a.fileName,
      bucket: a.storageBucket || 'message-attachments',
      contentType: a.contentType,
    }));

    setGalleryAttachments(viewerAttachments);
    setGalleryInitialIndex(startIndex);
    setGalleryUrls({});
    setGalleryOpen(true);

    // Resolve signed URLs for every media item (cached per storagePath)
    const urls: Record<number, string> = {};
    await Promise.all(
      media.map(async (a: any, i: number) => {
        try {
          const signedUrl = await queryClient.fetchQuery({
            queryKey: ['attachment-url', a.storagePath],
            queryFn: async () => {
              const response = await api.post('/api/files/generate-signed-url', {
                bucket: 'message-attachments',
                path: a.storagePath,
                expiresIn: 3600,
              });
              if (!response?.success) {
                throw new Error(response?.error || 'Failed to create signed URL');
              }
              return response.data.url;
            },
            staleTime: 50 * 60 * 1000,
          });
          if (signedUrl) urls[i] = signedUrl as string;
        } catch (error) {
          console.error('[Gallery] Failed to resolve signed URL:', error);
        }
      })
    );
    setGalleryUrls(urls);
  };

  // Share/save a gallery attachment via SMS or email (Save to Camera Roll and
  // Download are handled inside AttachmentViewerModal itself). Mirrors the
  // referral flow: Web Share API for images on mobile, clipboard/mailto fallback.
  const handleShareMessageAttachment = async (
    type: 'sms' | 'email' | 'download',
    attachment: any,
    url: string,
  ) => {
    if (!url) return;
    const isImage = attachment.contentType?.startsWith('image/') ||
      /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(attachment.name || '');

    if (type === 'sms') {
      if (navigator.share && isImage) {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          const file = new window.File([blob], attachment.name, { type: attachment.contentType || 'image/jpeg' });
          await navigator.share({ title: attachment.name, files: [file] });
          showSuccess('Shared successfully', 'Attachment shared');
        } catch (error) {
          navigator.clipboard.writeText(url);
          showSuccess('Link copied', 'Attachment link copied to clipboard. Paste in your messaging app.');
        }
      } else {
        navigator.clipboard.writeText(url);
        showSuccess('Link copied', 'Attachment link copied to clipboard. Paste in your SMS app.');
      }
    } else if (type === 'email') {
      if (navigator.share && isImage) {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          const file = new window.File([blob], attachment.name, { type: attachment.contentType || 'image/jpeg' });
          await navigator.share({ title: attachment.name, files: [file] });
          showSuccess('Shared successfully', 'Attachment shared via email');
        } catch (error) {
          const subject = encodeURIComponent(`Attachment: ${attachment.name}`);
          const body = encodeURIComponent(
            `Here is the attachment:\n\nFile: ${attachment.name}\nLink: ${url}\n\n` +
            `Note: Please download the file from the link above and attach it to your email.`
          );
          window.location.href = `mailto:?subject=${subject}&body=${body}`;
        }
      } else {
        const subject = encodeURIComponent(`Attachment: ${attachment.name}`);
        const body = encodeURIComponent(
          `Here is the attachment:\n\nFile: ${attachment.name}\nLink: ${url}\n\n` +
          `Note: Please download the file from the link above and attach it to your email.`
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      }
    }
  };

  // Handle send message with attachments
  const handleSendMessage = async () => {
    console.log('[SendMessage] Starting - selectedFiles:', selectedFiles.length, 'newMessage:', newMessage);

    if ((!newMessage.trim() && selectedFiles.length === 0) || !selectedConversation) {
      console.log('[SendMessage] Aborting - no content or no conversation');
      return;
    }

    setIsUploading(true);

    try {
      // Generate message content based on attachments
      let messageContent = newMessage.trim();
      if (!messageContent && selectedFiles.length > 0) {
        // If no text but has files, show file names
        if (selectedFiles.length === 1) {
          messageContent = `📎 ${selectedFiles[0].name}`;
        } else {
          messageContent = `📎 ${selectedFiles.length} files`;
        }
      }

      console.log('[SendMessage] Message content:', messageContent);

      // Send the message first
      const messageData: any = {
        recipientId: selectedConversation.partnerId,
        content: messageContent,
        referralId: selectedConversation.referralId ? Number(selectedConversation.referralId) : undefined,
      };

      console.log('[SendMessage] Sending message...');
      const messageResponse = await sendMessageMutation.mutateAsync(messageData);
      const messageId = messageResponse?.data?.id;
      console.log('[SendMessage] Message sent, ID:', messageId, 'Full response:', messageResponse);

      // Upload files if any
      if (selectedFiles.length > 0 && messageId) {
        console.log('[SendMessage] Uploading', selectedFiles.length, 'files...');
        const uploadPromises = selectedFiles.map((file, index) => {
          return uploadFile(file, messageId).then(result => {
            if (result.success) {
              setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
            }
            return result;
          });
        });

        const results = await Promise.all(uploadPromises);
        const failed = results.filter(r => !r.success);

        console.log('[SendMessage] Upload results - success:', results.length - failed.length, 'failed:', failed.length);

        if (failed.length > 0) {
          // Show the specific reason for the first failure; if multiple
          // files failed, the user can retry — repeating every reason in a
          // toast stack adds noise without helping them decide what to do.
          const firstReason = failed.find(f => f.error)?.error || 'Upload failed.';
          const suffix = failed.length > 1 ? ` (${failed.length} files failed)` : '';
          showError('Upload failed', `${firstReason}${suffix}`);
        } else if (selectedFiles.length > 0) {
          // Invalidate attachments query to refetch and display new attachments
          queryClient.invalidateQueries({
            queryKey: ["attachments", selectedConversation?.id],
            refetchType: 'active'
          });
        }
      } else {
        console.log('[SendMessage] No files to upload or no messageId');
      }

      // Clear state
      setNewMessage('');
      setSelectedFiles([]);
      setUploadProgress({});
    } catch (error: any) {
      showError(error.message || 'Failed to send message');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle starting a new conversation with a partner (or opening existing one).
  // The placeholder id matches the eventual server-side direct-thread id
  // (`${chat_id}` with no referral suffix). That keeps the React Query cache
  // key stable across the first send, so the new message appears immediately
  // and isn't lost when the conversation list refetches.
  const handleStartNewConversation = (partner: any) => {
    const partnerName = [partner.firstName, partner.lastName].filter(Boolean).join(' ') || partner.company || 'Unknown';
    // Use partnerId which is the actual user ID, not id which is the connection ID
    const actualPartnerId = partner.partnerId || partner.id;

    // Look up an existing *direct* (non-referral) conversation. The `!conv.referralId`
    // check is what prevents us from accidentally re-opening the user's
    // referral thread when they meant to start a direct conversation.
    const existingConversation = conversations.find(
      conv => conv.partnerId === actualPartnerId && !conv.referralId
    );

    if (existingConversation) {
      // Open existing conversation
      setSelectedConversation(existingConversation);
      setShowNewConversationModal(false);
      return;
    }

    const rawChatId = user?.id ? computeChatId(user.id, actualPartnerId) : `new-${actualPartnerId}`;
    const newConversation: Message = {
      id: rawChatId,
      chatId: rawChatId,
      type: 'conversation',
      partnerId: actualPartnerId,
      partnerName,
      partnerTrade: partner.trade,
      partnerAvatar: partner.profileImageUrl,
      lastMessage: 'Start a new conversation',
      lastMessageTime: new Date().toISOString(),
      unreadCount: 0,
      isNewConversation: true
    };

    setSelectedConversation(newConversation);
    setShowNewConversationModal(false);
  };

  // Lookup referral display details for a conversation — checks local map first, then referralsData
  const getReferralDetails = (conv: Message) => {
    const mapEntry = referralChatMap.current[conv.partnerId];
    if (mapEntry) return mapEntry;
    if (referralsData && conv.referralId) {
      const referral = (referralsData as any[]).find(
        (r: any) => r.id === conv.referralId || r.id === Number(conv.referralId)
      );
      if (referral) {
        return {
          referralId: referral.id,
          clientName: referral.clientName || referral.customerName || 'Contact',
          jobDescription: referral.jobDescription || '',
          senderName: referral.senderName || '',
        };
      }
    }
    return null;
  };

  // Get the full referral object from referralsData by referralId (for the modal)
  const getFullReferral = (referralId: string | number | undefined) => {
    if (!referralId || !referralsData) return null;
    return (referralsData as any[]).find(
      (r: any) => r.id === referralId || r.id === Number(referralId)
    ) ?? null;
  };

  // Count unread and requests
  const unreadCount = conversations.filter((c) => c.unreadCount > 0).length;
  const requestCount = conversations.filter((c) => c.type === "connection_request" && c.status === "pending").length;

  return (
    <>
    <AppLayout flushBottomNav={!!selectedConversation}>
      <div className={selectedConversation ? "h-full" : "container mx-auto px-4 max-w-4xl py-6"}>
        {/* Header — hidden when a conversation is open */}
        {!selectedConversation && (
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="h-8 w-8 text-primary" />
            <div className="flex-1">
              <h1 className="text-2xl font-bold">Messages</h1>
              <p className="text-sm text-muted-foreground">
                {unreadCount > 0 && `${unreadCount} unread`}
                {unreadCount > 0 && requestCount > 0 && " · "}
                {requestCount > 0 && `${requestCount} pending request${requestCount !== 1 ? "s" : ""}`}
                {unreadCount === 0 && requestCount === 0 && "All caught up"}
              </p>
            </div>
          </div>
        )}

        <div className={selectedConversation ? "h-full" : ""}>
          {/* Conversations List */}
          <div className={selectedConversation ? "hidden" : ""}>
            {/* New Conversation Button */}
            <Button
              className="w-full mb-4"
              onClick={() => {
                setNewConvTab(activeTab === "referrals" ? "referrals" : "partners");
                setShowNewConversationModal(true);
              }}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              New Conversation
            </Button>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-4">
              {(["all", "partners", "referrals"] as const).map((tab) => {
                const labels = { all: "All", partners: "Partners", referrals: "Referrals" };
                const icons = { all: <MessageSquare className="w-3.5 h-3.5" />, partners: <Users className="w-3.5 h-3.5" />, referrals: <FileText className="w-3.5 h-3.5" /> };
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab
                        ? "border-primary text-primary"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {icons[tab]}
                    {labels[tab]}
                  </button>
                );
              })}
            </div>

            {/* Conversation list — shared by All, Partners, and Referrals tabs */}
            {isPending ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                {activeTab === "referrals" ? (
                  <>
                    <FileText className="h-10 w-10 text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">No referral conversations yet</p>
                    <p className="text-xs text-gray-400 mt-1">Start a referral chat via New Conversation</p>
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-10 w-10 text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">No messages found</p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredConversations.map((conv) => (
                  <button
                    key={conv.id}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors ${
                      selectedConversation?.id === conv.id ? "bg-primary/10 border border-primary/20" : "hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      setSelectedConversation(conv);
                      if (conv.type === "connection_request" && conv.unreadCount > 0) {
                        markConversationAsReadMutation.mutate(conv.id);
                      }
                    }}
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      {conv.partnerAvatar ? (
                        <img
                          src={conv.partnerAvatar}
                          alt={conv.partnerName || "User"}
                          className="w-11 h-11 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold ${conv.referralId ? "bg-blue-100 text-blue-600" : "bg-primary/10 text-primary"}`}>
                          {conv.partnerName?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      {conv.type === "connection_request" && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                          <UserPlus className="h-2.5 w-2.5 text-white" />
                        </div>
                      )}
                      {conv.referralId && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                          <FileText className="h-2.5 w-2.5 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1 mb-0.5">
                        <div className={`flex items-center gap-1.5 min-w-0 ${conv.unreadCount > 0 ? "font-bold text-gray-900" : "font-medium text-gray-800"}`}>
                          <span className="text-sm truncate">
                            {conv.referralId
                              ? `Referral - ${getReferralDetails(conv)?.clientName ?? conv.partnerName}`
                              : conv.partnerName}
                            {conv.isOrgAdmin && <span className="text-xs text-amber-600 ml-1">(Admin)</span>}
                          </span>
                          {conv.referralId && (
                            <span
                              className="flex-shrink-0 text-[12px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 leading-none"
                            >
                              Referral
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-gray-400 flex-shrink-0">{formatTime(conv.lastMessageTime)}</span>
                      </div>
                      {conv.referralId ? (
                        (() => {
                          const rd = getReferralDetails(conv);
                          const subtitle = [rd?.jobDescription, rd?.senderName ? `via ${rd.senderName}` : ''].filter(Boolean).join(' - ');
                          return subtitle ? <p className="text-xs text-blue-500 truncate leading-none mb-0.5">{subtitle}</p> : null;
                        })()
                      ) : conv.partnerTrade ? (
                        <p className="text-xs text-gray-400 truncate leading-none mb-0.5">{conv.partnerTrade}</p>
                      ) : null}
                      <p className={`text-xs truncate ${conv.unreadCount > 0 ? "text-gray-700" : "text-gray-400"}`}>
                        {conv.lastMessage}
                      </p>
                    </div>

                    {/* Badge */}
                    {conv.unreadCount > 0 && (
                      <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold text-white">{conv.unreadCount}</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Chat View */}
          {selectedConversation && (
              <Card className="h-full flex flex-col rounded-none border-0 md:rounded-xl md:border">
                {/* Chat Header */}
                <div className="p-4 border-b">
                  <div className="flex items-center gap-3 mb-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedConversation(null)}
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (selectedConversation.partnerId && !selectedConversation.partnerDeleted) {
                          setProfilePreviewPartnerId(selectedConversation.partnerId);
                          setShowProfilePreview(true);
                        }
                      }}
                      disabled={!selectedConversation.partnerId || selectedConversation.partnerDeleted}
                      aria-label={
                        selectedConversation.partnerDeleted
                          ? "This account has been deleted"
                          : `View ${selectedConversation.partnerName || "user"}'s profile`
                      }
                      className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 hover:opacity-80 transition-opacity disabled:cursor-default disabled:hover:opacity-100"
                    >
                      {selectedConversation.partnerAvatar ? (
                        <img
                          src={selectedConversation.partnerAvatar}
                          alt={selectedConversation.partnerName || "User"}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                      )}
                    </button>
                    <div
                      className={`flex-1 ${selectedConversation.referralId ? 'cursor-pointer' : ''}`}
                      onClick={() => {
                        if (selectedConversation.referralId) {
                          const full = getFullReferral(selectedConversation.referralId);
                          if (full) setReferralModalData(full);
                        }
                      }}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3
                          className={`font-medium ${!selectedConversation.referralId && !selectedConversation.partnerDeleted ? 'cursor-pointer hover:text-primary transition-colors' : ''}`}
                          onClick={(e) => {
                            if (!selectedConversation.referralId && !selectedConversation.partnerDeleted) {
                              e.stopPropagation();
                              // Set partner ID to trigger profile fetch
                              setProfilePreviewPartnerId(selectedConversation.partnerId);
                              setShowProfilePreview(true);
                            }
                          }}
                        >
                          {selectedConversation.referralId
                            ? `Referral - ${getReferralDetails(selectedConversation)?.clientName ?? selectedConversation.partnerName}`
                            : selectedConversation.partnerName}
                          {selectedConversation.isOrgAdmin && <span className="text-xs text-amber-600 ml-1">(Admin)</span>}
                        </h3>
                        {selectedConversation.referralId && (
                          <button
                            className="text-[13px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800 hover:shadow-sm transition-all leading-none"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent double trigger
                              const full = getFullReferral(selectedConversation.referralId);
                              if (full) setReferralModalData(full);
                            }}
                          >
                            Referral
                          </button>
                        )}
                      </div>
                      {selectedConversation.referralId ? (
                        (() => {
                          const rd = getReferralDetails(selectedConversation);
                          const subtitle = [rd?.jobDescription, rd?.senderName ? `via ${rd.senderName}` : ''].filter(Boolean).join(' - ');
                          return subtitle ? <p className="text-xs text-blue-500">{subtitle}</p> : null;
                        })()
                      ) : selectedConversation.partnerDeleted ? (
                        <p className="text-xs text-muted-foreground">
                          {selectedConversation.partnerFormerCompany
                            ? `Deleted account - formerly ${selectedConversation.partnerFormerCompany}`
                            : "Deleted account"}
                        </p>
                      ) : selectedConversation.partnerTrade ? (
                        <p className="text-xs text-muted-foreground">
                          {selectedConversation.partnerTrade}
                        </p>
                      ) : null}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {/* Report — Guideline 1.2 asks for reporting where the content is, not
                            only on a profile screen. Hidden for a deleted account: there is no
                            longer anyone to moderate. */}
                        {selectedConversation.type === "conversation" &&
                          !selectedConversation.partnerDeleted && (
                            <DropdownMenuItem
                              className="text-red-600 focus:text-red-600"
                              onClick={() => setShowReportConversation(true)}
                            >
                              <Flag className="h-4 w-4 mr-2" />
                              Report Conversation
                            </DropdownMenuItem>
                          )}
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-600"
                          onClick={() => setShowDeleteConfirm(true)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Conversation
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                </div>

                {/* Connection Request View */}
                {selectedConversation.type === "connection_request" &&
                  selectedConversation.status === "pending" ? (
                  <div className="flex-1 flex flex-col items-center justify-center p-6">
                    <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                      <UserPlus className="h-10 w-10 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">Connection Request</h3>
                    <p className="text-center text-muted-foreground mb-2">
                      {selectedConversation.partnerName} wants to connect with you
                    </p>
                    <p className="text-center text-sm text-muted-foreground mb-6 max-w-sm">
                      "{selectedConversation.lastMessage}"
                    </p>
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() =>
                          declineConnectionMutation.mutate(selectedConversation.partnerId)
                        }
                        disabled={declineConnectionMutation.isPending}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Decline
                      </Button>
                      <Button
                        onClick={() =>
                          acceptConnectionMutation.mutate(selectedConversation.partnerId)
                        }
                        disabled={acceptConnectionMutation.isPending}
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Accept
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Chat Messages */}
                    {/* min-h-0 is load-bearing: without it this flex-1 child grows to
                        fit all messages instead of scrolling internally, pushing the
                        composer below the fold and making the input/send/attach
                        buttons unreachable. */}
                    <div
                      ref={messagesContainerRef}
                      onScroll={handleMessagesScroll}
                      onWheel={handleUserScrollIntent}
                      onTouchMove={handleUserScrollIntent}
                      style={{ overflowAnchor: 'none' }}
                      className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4"
                    >
                      <div ref={messagesContentRef} className="space-y-1">
                      {chatMessages?.map((msg, index) => {
                        const isLastMessage = index === (chatMessages?.length || 0) - 1;
                        const showTime = expandedTimeMessageId === msg.id || isLastMessage;
                        const prevMsg = index > 0 ? chatMessages[index - 1] : null;
                        const showDateSeparator = !prevMsg || getMessageDateKey(msg.timestamp) !== getMessageDateKey(prevMsg.timestamp);

                        return (
                          <div key={msg.id}>
                            {/* Date Separator */}
                            {showDateSeparator && (
                              <div className="flex items-center justify-center my-4">
                                <div className="bg-muted text-muted-foreground text-xs px-3 py-1 rounded-full">
                                  {formatDateSeparator(msg.timestamp)}
                                </div>
                              </div>
                            )}

                            {/* Message Bubble */}
                            {msg.messageType === 'system' ? (
                              /* System Message - Centered, no interaction */
                              <div className="flex justify-center my-3">
                                <div className="bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400 text-sm px-4 py-2 rounded-full border border-blue-200 dark:border-blue-800 max-w-[80%] text-center">
                                  {msg.content}
                                </div>
                              </div>
                            ) : (
                              <div className={`flex ${msg.isOwn ? "justify-end" : "justify-start"} mb-1`}>
                                <div className="relative max-w-[60%] group">
                                  {!msg.isOwn && msg.senderName && (
                                    <p className="text-xs font-medium text-muted-foreground mb-0.5 px-1">
                                      {msg.senderName}
                                    </p>
                                  )}
                                <Popover
                                  open={activeMessageMenu === msg.id}
                                  onOpenChange={(open) => setActiveMessageMenu(open ? msg.id : null)}
                                >
                                  <PopoverTrigger asChild>
                                    <div
                                      className={`rounded-lg px-4 py-2 cursor-pointer transition-opacity ${
                                        msg.isOwn
                                          ? "bg-primary text-primary-foreground"
                                          : "bg-muted"
                                      }`}
                                      onClick={() => {
                                        if (editingMessageId !== msg.id) {
                                          // Toggle time on click (unless in edit mode)
                                          setExpandedTimeMessageId(
                                            expandedTimeMessageId === msg.id ? null : msg.id
                                          );
                                        }
                                      }}
                                      onContextMenu={(e) => {
                                        e.preventDefault();
                                        setActiveMessageMenu(msg.id);
                                      }}
                                    >
                                      {editingMessageId === msg.id ? (
                                        <div className="space-y-2">
                                          <div className="relative">
                                            <Textarea
                                              value={editContent}
                                              onChange={(e) => {
                                                const value = e.target.value;
                                                if (value.length <= MAX_MESSAGE_LENGTH) {
                                                  setEditContent(value);
                                                }
                                              }}
                                              className={`min-h-[120px] max-h-[240px] resize-none bg-background text-foreground text-sm pr-16 ${
                                                editContent.length > MAX_MESSAGE_LENGTH * 0.9
                                                  ? 'border-amber-500 focus-visible:ring-amber-500'
                                                  : editContent.length > MAX_MESSAGE_LENGTH
                                                  ? 'border-red-500 focus-visible:ring-red-500'
                                                  : ''
                                              }`}
                                              rows={6}
                                              autoFocus
                                              onKeyDown={(e) => {
                                                if (e.key === "Enter" && !e.shiftKey) {
                                                  e.preventDefault();
                                                  if (editContent.trim() && editContent.length <= MAX_MESSAGE_LENGTH) {
                                                    editMessageMutation.mutate({
                                                      messageId: msg.id,
                                                      messageContent: editContent.trim(),
                                                    });
                                                  }
                                                }
                                                if (e.key === "Escape") {
                                                  setEditingMessageId(null);
                                                  setEditContent("");
                                                }
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            />
                                            <div className={`absolute bottom-1 right-1 text-[10px] ${
                                              editContent.length > MAX_MESSAGE_LENGTH
                                                ? 'text-red-500 font-semibold'
                                                : editContent.length > MAX_MESSAGE_LENGTH * 0.9
                                                ? 'text-amber-500 font-medium'
                                                : 'text-muted-foreground'
                                            }`}>
                                              {editContent.length}/{MAX_MESSAGE_LENGTH}
                                            </div>
                                          </div>
                                          <div className="flex gap-1 justify-end">
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-6 px-2 text-xs"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingMessageId(null);
                                                setEditContent("");
                                              }}
                                            >
                                              Cancel
                                            </Button>
                                            <Button
                                              size="sm"
                                              className="h-6 px-2 text-xs"
                                              disabled={!editContent.trim() || editMessageMutation.isPending || editContent.length > MAX_MESSAGE_LENGTH}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                editMessageMutation.mutate({
                                                  messageId: msg.id,
                                                  messageContent: editContent.trim(),
                                                });
                                              }}
                                            >
                                              Save
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <p className="text-sm whitespace-pre-wrap break-words overflow-wrap-anywhere">{msg.content}</p>

                                          {/* Display Attachments */}
                                          {messageAttachments?.[msg.id] && messageAttachments[msg.id].length > 0 && (
                                            <div className="mt-2 space-y-2">
                                              {messageAttachments[msg.id].map((attachment: any) => (
                                                <AttachmentDisplay
                                                  key={attachment.id}
                                                  attachment={attachment}
                                                  isOwn={msg.isOwn}
                                                  siblingAttachments={messageAttachments[msg.id]}
                                                  onOpenGallery={openImageGallery}
                                                  onLoaded={handleMediaLoaded}
                                                />
                                              ))}
                                            </div>
                                          )}

                                          {msg.editedAt && (
                                            <p className={`text-[10px] mt-0.5 italic ${
                                              msg.isOwn ? "text-primary-foreground/50" : "text-muted-foreground/70"
                                            }`}>
                                              (edited)
                                            </p>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    side="top"
                                    align={msg.isOwn ? "end" : "start"}
                                    className="w-auto p-1"
                                    onOpenAutoFocus={(e) => e.preventDefault()}
                                  >
                                    <div className="flex flex-col gap-0.5 min-w-[140px]">
                                      {msg.isOwn && (
                                        <>
                                          <button
                                            className="flex items-center gap-2 w-full px-3 py-1.5 text-sm rounded hover:bg-muted text-left"
                                            onClick={() => {
                                              setEditingMessageId(msg.id);
                                              setEditContent(msg.content);
                                              setActiveMessageMenu(null);
                                            }}
                                          >
                                            <Pencil className="h-3.5 w-3.5" />
                                            Edit
                                          </button>
                                          <button
                                            className="flex items-center gap-2 w-full px-3 py-1.5 text-sm rounded hover:bg-muted text-left text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                                            onClick={() => {
                                              deleteMessageMutation.mutate(msg.id);
                                              setActiveMessageMenu(null);
                                            }}
                                          >
                                            <Trash2 className="h-3.5 w-3.5" />
                                            Delete
                                          </button>
                                        </>
                                      )}
                                      <button
                                        className="flex items-center gap-2 w-full px-3 py-1.5 text-sm rounded hover:bg-muted text-left"
                                        onClick={() => {
                                          setForwardingMessageId(msg.id);
                                          setShowForwardDialog(true);
                                          setActiveMessageMenu(null);
                                        }}
                                      >
                                        <Forward className="h-3.5 w-3.5" />
                                        Forward
                                      </button>
                                      <div className="border-t my-1" />
                                      <div className="px-2 py-1">
                                        <p className="text-xs text-muted-foreground mb-1.5">React</p>
                                        <div className="flex flex-wrap gap-1">
                                          {REACTION_EMOJIS.map((emoji) => {
                                            const hasReacted = msg.reactions?.some(
                                              (r) => r.emoji === emoji && r.userId === user?.id
                                            );
                                            return (
                                              <button
                                                key={emoji}
                                                className={`text-lg hover:scale-125 transition-transform p-0.5 rounded ${
                                                  hasReacted ? "bg-primary/20" : ""
                                                }`}
                                                onClick={() => {
                                                  toggleReactionMutation.mutate({
                                                    messageId: msg.id,
                                                    emoji,
                                                  });
                                                }}
                                              >
                                                {emoji}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  </PopoverContent>
                                </Popover>

                                {/* Reaction badges */}
                                {msg.reactions && msg.reactions.length > 0 && (
                                  <div className={`flex gap-0.5 mt-0.5 flex-wrap ${msg.isOwn ? "justify-end" : "justify-start"}`}>
                                    <TooltipProvider delayDuration={200}>
                                      {(() => {
                                        // Group reactions by emoji with user names
                                        const reactionGroups: Record<string, { count: number; users: string[] }> = {};
                                        for (const r of msg.reactions || []) {
                                          if (!reactionGroups[r.emoji]) reactionGroups[r.emoji] = { count: 0, users: [] };
                                          reactionGroups[r.emoji].count += 1;
                                          reactionGroups[r.emoji].users.push(r.userName);
                                        }
                                        return Object.entries(reactionGroups).map(([emoji, data]) => {
                                          const hasReacted = msg.reactions?.some(
                                            (r: MessageReaction) => r.emoji === emoji && r.userId === user?.id
                                          );
                                          return (
                                            <Tooltip key={emoji}>
                                              <TooltipTrigger asChild>
                                                <button
                                                  className={`inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full border transition-colors ${
                                                    hasReacted
                                                      ? "bg-primary/10 border-primary/30"
                                                      : "bg-background border-border hover:bg-muted"
                                                  }`}
                                                  onClick={() =>
                                                    toggleReactionMutation.mutate({
                                                      messageId: msg.id,
                                                      emoji,
                                                    })
                                                  }
                                                >
                                                  <span className="text-sm">{emoji}</span>
                                                  {data.count > 1 && <span className="text-muted-foreground">{data.count}</span>}
                                                </button>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" className="max-w-[200px]">
                                                <p className="text-xs">{data.users.join(", ")}</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          );
                                        });
                                      })()}
                                    </TooltipProvider>
                                  </div>
                                )}
                              </div>
                            </div>
                            )}

                            {/* Collapsible timestamp */}
                            {msg.messageType !== 'system' && (
                              <div
                                className={`flex ${msg.isOwn ? "justify-end" : "justify-start"} overflow-hidden transition-all duration-200 ease-in-out ${
                                  showTime ? "max-h-6 opacity-100" : "max-h-0 opacity-0"
                                }`}
                              >
                                <p className="text-[10px] text-muted-foreground px-4">
                                  {formatMessageTime(msg.timestamp)}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Invisible div to scroll to */}
                      <div ref={messagesEndRef} />
                      </div>
                    </div>

                    {/* Message Input.
                        Bug 23: when the partner has deleted their account the thread stays
                        readable (it is this user's history too) but the composer is replaced
                        with a notice — there is no account left to deliver to, and the send
                        endpoint rejects it with RECIPIENT_DELETED anyway. */}
                    {selectedConversation.partnerDeleted ? (
                      <div className="p-4 border-t">
                        <p
                          role="status"
                          className="text-sm text-muted-foreground text-center bg-muted/50 rounded-lg px-4 py-3"
                        >
                          This account has been deleted. You can still read this conversation, but
                          you can no longer send messages to it.
                        </p>
                      </div>
                    ) : (
                    <div className="p-4 border-t">
                      {/* File Previews */}
                      {selectedFiles.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-2">
                          {selectedFiles.map((file, index) => (
                            <div
                              key={index}
                              className="relative group flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg border border-gray-200"
                            >
                              {file.type.startsWith('image/') ? (
                                <ImageIcon className="h-4 w-4 text-blue-600" />
                              ) : (
                                <File className="h-4 w-4 text-gray-600" />
                              )}
                              <span className="text-sm text-gray-700 max-w-[150px] truncate">
                                {file.name}
                              </span>
                              <span className="text-xs text-gray-500">
                                ({(file.size / 1024).toFixed(1)} KB)
                              </span>
                              <button
                                onClick={() => handleRemoveFile(index)}
                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label="Remove file"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2">
                        {/* Hidden file input */}
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept={[
                            // Media — generic wildcards plus explicit
                            // extensions, because some platforms (Windows in
                            // particular) hide .mov from `video/*`.
                            'image/*',
                            'video/*',
                            ...IMAGE_FILE_EXTENSIONS,
                            ...VIDEO_FILE_EXTENSIONS,
                            // Documents + archives
                            '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt',
                            '.zip', '.gz', '.tar', '.tar.gz', '.tgz', '.7z',
                          ].join(',')}
                          onChange={handleFileSelect}
                          className="hidden"
                        />

                        {/* Attachment button */}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isUploading}
                                aria-label="Attach files"
                              >
                                <Paperclip className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">Attach files</p>
                              <p className="text-[10px] text-muted-foreground">Images: 25MB · Docs: 100MB · Videos: 250MB</p>
                              <p className="text-[10px] text-muted-foreground mt-1">Max video size: 250MB. This is usually 2–3 minutes on standard phone settings, less if recording 4K/60fps.</p>
                              <p className="text-[10px] text-muted-foreground mt-1">Large images auto-compress</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        {/* Native camera (Card 20 task B). The paperclip stays the way
                            to attach documents, videos and multiple files —
                            @capacitor/camera returns exactly one image — so this is an
                            additional action, not a replacement. Native only; on web
                            the file input's own "Take Photo" already reaches the
                            camera. */}
                        {isNativeCaptureAvailable() && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() =>
                              void captureImageInto(
                                (file) => processSelectedFiles([file]),
                                showError,
                                { source: 'camera' },
                              )
                            }
                            disabled={isUploading}
                            aria-label="Take a photo"
                          >
                            <Camera className="h-4 w-4" />
                          </Button>
                        )}

                        <div className="flex-1 relative">
                          <Textarea
                            ref={messageInputRef}
                            placeholder="Type a message..."
                            value={newMessage}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow typing but prevent exceeding limit
                              if (value.length <= MAX_MESSAGE_LENGTH) {
                                setNewMessage(value);
                              }
                            }}
                            className={`min-h-[44px] max-h-60 resize-none pr-16 overflow-y-auto ${
                              newMessage.length > MAX_MESSAGE_LENGTH * 0.9
                                ? 'border-amber-500 focus-visible:ring-amber-500'
                                : newMessage.length > MAX_MESSAGE_LENGTH
                                ? 'border-red-500 focus-visible:ring-red-500'
                                : ''
                            }`}
                            rows={1}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                if (newMessage.length <= MAX_MESSAGE_LENGTH) {
                                  handleSendMessage();
                                }
                              }
                            }}
                            disabled={isUploading}
                          />
                          <div className={`absolute bottom-2 right-2 text-xs ${
                            newMessage.length > MAX_MESSAGE_LENGTH
                              ? 'text-red-500 font-semibold'
                              : newMessage.length > MAX_MESSAGE_LENGTH * 0.9
                              ? 'text-amber-500 font-medium'
                              : 'text-muted-foreground'
                          }`}>
                            {newMessage.length}/{MAX_MESSAGE_LENGTH}
                          </div>
                        </div>
                        <Button
                          onClick={handleSendMessage}
                          disabled={
                            (!newMessage.trim() && selectedFiles.length === 0) ||
                            isUploading ||
                            newMessage.length > MAX_MESSAGE_LENGTH
                          }
                          aria-label="Send"
                        >
                          {isUploading ? (
                            <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    )}
                  </>
                )}
              </Card>
          )}
        </div>

        {/* New Conversation Modal */}
        {showNewConversationModal && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
              onClick={() => { setShowNewConversationModal(false); setExpandedNewConvReferralId(null); }}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="new-conversation-title"
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto animate-slide-up overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h2 id="new-conversation-title" className="text-xl font-bold text-white">
                      New Conversation
                    </h2>
                    <button
                      onClick={() => { setShowNewConversationModal(false); setExpandedNewConvReferralId(null); }}
                      className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
                      aria-label="Close modal"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </div>
                  {/* Modal tabs */}
                  <div className="flex gap-1 mt-3">
                    {(["partners", "referrals"] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => { setNewConvTab(t); setExpandedNewConvReferralId(null); }}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          newConvTab === t ? "bg-white text-blue-600" : "text-white/80 hover:bg-white/20"
                        }`}
                      >
                        {t === "partners" ? <Users className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                        {t === "partners" ? "Partners" : "Referrals"}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Content */}
                <div className="max-h-[60vh] overflow-y-auto p-4">
                  {newConvTab === "referrals" ? (
                    /* Referrals tab — pick referral → pick partner */
                    !referralsData || referralsData.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p className="text-sm">No referrals found</p>
                        <p className="text-xs mt-1">Send a referral first to start a referral chat</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {referralsData.map((referral: any) => {
                          const clientName = referral.clientName || referral.customerName || "Contact";
                          const isSent = referral.senderId === user?.id;
                          const partnerId = isSent ? referral.receiverId : referral.senderId;
                          const partnerName = isSent ? referral.receiverName : referral.senderName;
                          const partnerTrade = isSent ? referral.receiverTrade : referral.senderTrade;
                          const isExpanded = expandedNewConvReferralId === referral.id;

                          return (
                            <div key={referral.id} className={`rounded-xl border transition-all ${isExpanded ? "border-blue-300 bg-blue-50" : "border-gray-200 bg-white"}`}>
                              {/* Referral row */}
                              <button
                                className="w-full flex items-center gap-3 p-3 text-left"
                                onClick={() => setExpandedNewConvReferralId(isExpanded ? null : referral.id)}
                              >
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                  <FileText className="w-5 h-5 text-blue-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <p className="text-sm font-semibold text-gray-900 truncate">{clientName}</p>
                                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                      referral.status === "completed" ? "bg-blue-100 text-blue-700" :
                                      referral.status === "accepted" ? "bg-green-100 text-green-700" :
                                      referral.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                                      "bg-gray-100 text-gray-600"
                                    }`}>{referral.status}</span>
                                  </div>
                                  <p className="text-xs text-gray-500 truncate">{isSent ? "To" : "From"} {partnerName}</p>
                                </div>
                                {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                              </button>

                              {/* Expanded partner row */}
                              {isExpanded && (
                                <div className="border-t border-blue-200 mx-3 mb-2">
                                  <button
                                    className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-blue-100 rounded-lg px-1 transition-colors"
                                    onClick={() => handleStartReferralChat(referral, partnerId, partnerName, partnerTrade)}
                                  >
                                    <div className="w-8 h-8 rounded-full bg-white border border-blue-200 flex items-center justify-center flex-shrink-0">
                                      <User className="w-4 h-4 text-blue-500" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate">{partnerName || "Partner"}</p>
                                      {partnerTrade && <p className="text-xs text-gray-400 truncate">{partnerTrade}</p>}
                                    </div>
                                    <MessageSquare className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  ) : (
                    /* Partners tab — pick a connection */
                    isLoadingConnections ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      </div>
                    ) : !connectionsData || connectionsData.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <User className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p className="text-sm">No connections found</p>
                        <p className="text-xs mt-1">Connect with partners first to start messaging</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {connectionsData.map((partner: any) => {
                          // Check if already have an individual conversation with this partner (not referral-based)
                          // Use partnerId which is the actual user ID, not id which is the connection ID
                          const actualPartnerId = partner.partnerId || partner.id;
                          const hasExistingConversation = conversations.some(
                            conv => conv.partnerId === actualPartnerId && !conv.referralId
                          );
                          const partnerName = [partner.firstName, partner.lastName].filter(Boolean).join(' ') || partner.company || 'Unknown';

                          return (
                            <button
                              key={actualPartnerId}
                              onClick={() => handleStartNewConversation(partner)}
                              className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all group"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                                  <User className="w-5 h-5 text-blue-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 truncate">
                                    {partnerName}
                                    {orgAdminId && actualPartnerId === orgAdminId && (
                                      <span className="text-xs text-amber-600 ml-1">(Admin)</span>
                                    )}
                                  </p>
                                  {partner.trade && (
                                    <p className="text-sm text-gray-500 truncate">
                                      {partner.trade}
                                    </p>
                                  )}
                                  {hasExistingConversation && (
                                    <p className="text-xs text-blue-500 mt-0.5">
                                      Open existing conversation
                                    </p>
                                  )}
                                </div>
                                <MessageSquare className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>

    {/* Delete Conversation Confirmation */}
    {/* Referral Detail Modal */}
    {referralModalData && (
      <ReferralDetailModal
        referral={referralModalData}
        isOpen={!!referralModalData}
        onClose={() => setReferralModalData(null)}
      />
    )}

    {/* Swipeable attachment gallery for message media */}
    <AttachmentViewerModal
      isOpen={galleryOpen}
      onClose={() => setGalleryOpen(false)}
      attachments={galleryAttachments}
      attachmentUrls={galleryUrls}
      initialIndex={galleryInitialIndex}
      onShare={handleShareMessageAttachment}
    />

    {/* Profile Preview Modal */}
    {profilePreviewPartnerId && (
      <ProfilePreviewModal
        isOpen={showProfilePreview}
        onClose={() => {
          setShowProfilePreview(false);
          setProfilePreviewPartnerId(null);
        }}
        partner={((): any => {
          const base = partnerProfileData
            ? toProfilePreviewPartner(partnerProfileData)
            : {
                // Loading stub: pass the id so the modal doesn't fall into
                // own-profile mode (`!partner && !!currentUser`) while the
                // public-profile fetch is in flight.
                id: profilePreviewPartnerId,
                firstName: selectedConversation?.partnerName?.split(' ')[0] ?? '',
                lastName: selectedConversation?.partnerName?.split(' ').slice(1).join(' ') ?? '',
                profileImageUrl: selectedConversation?.partnerAvatar,
                status: 'connected' as const,
              };
          // Merge connection-derived state so the hamburger and star reflect
          // the partner's actual connection record (favorite flag, connectionId).
          return {
            ...base,
            connectionId: partnerConnection.connectionId,
            isFavorite: partnerConnection.isFavorite,
          };
        })()}
        currentUserOrganizationId={(user as any)?.organizationId}
        // Messaging is always between connected partners — match NetworkPage's
        // pattern of gating visibility on the partner shape (so the star
        // appears immediately) and gating the action handler on the
        // connectionId being loaded.
        showFavoriteButton={true}
        showMenuButton={true}
        onToggleFavorite={
          partnerConnection.connectionId ? partnerConnection.toggleFavorite : undefined
        }
        showActionButtons={true}
        onSendReferral={() => {
          if (!profilePreviewPartnerId) return;
          setShowProfilePreview(false);
          setProfilePreviewPartnerId(null);
          navigate(`/send-referral?partnerId=${profilePreviewPartnerId}`);
        }}
        onSendMessage={() => {
          // Already inside the messages page — just dismiss the preview.
          setShowProfilePreview(false);
          setProfilePreviewPartnerId(null);
        }}
        onPartnerRemoved={() => {
          queryClient.invalidateQueries({ queryKey: ['/api/workflows/connections'] });
          setShowProfilePreview(false);
          setProfilePreviewPartnerId(null);
        }}
      />
    )}

    {selectedConversation?.partnerId && (
      <ReportContentModal
        open={showReportConversation}
        onClose={() => setShowReportConversation(false)}
        reportedUserId={selectedConversation.partnerId}
        reportedUserName={selectedConversation.partnerName}
        contentType="message"
        canBlock
        onReported={(alsoBlocked) => {
          // A block removes the connection the thread hangs off, so the list behind this is stale.
          if (alsoBlocked) {
            queryClient.invalidateQueries({ queryKey: ['/api/workflows/connections'] });
            setSelectedConversation(null);
          }
        }}
      />
    )}

    <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Conversation</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this conversation with{" "}
            <span className="font-medium">{selectedConversation?.partnerName}</span>?
            This will hide the conversation from your view only. The other person will still be able to see it.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 hover:bg-red-700"
            onClick={() => {
              if (selectedConversation) {
                deleteConversationMutation.mutate(selectedConversation.id);
              }
            }}
            disabled={deleteConversationMutation.isPending}
          >
            {deleteConversationMutation.isPending ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Forward Message Dialog */}
    <Dialog open={showForwardDialog} onOpenChange={setShowForwardDialog}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Forward Message</DialogTitle>
        </DialogHeader>
        <div className="max-h-[300px] overflow-y-auto space-y-2">
          {!forwardConnectionsData || forwardConnectionsData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No connections to forward to
            </p>
          ) : (
            forwardConnectionsData
              .filter((c: any) => c.id !== selectedConversation?.partnerId)
              .map((partner: any) => {
                const name = [partner.firstName, partner.lastName].filter(Boolean).join(" ") || partner.company || "Unknown";
                return (
                  <button
                    key={partner.id}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors text-left"
                    onClick={() => {
                      if (forwardingMessageId) {
                        forwardMessageMutation.mutate({
                          recipientId: partner.id,
                          originalMessageId: forwardingMessageId,
                        });
                      }
                    }}
                    disabled={forwardMessageMutation.isPending}
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{name}</p>
                      {partner.trade && (
                        <p className="text-xs text-muted-foreground truncate">{partner.trade}</p>
                      )}
                    </div>
                  </button>
                );
              })
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default MessagesPage;
